// ============================================================
// HoverChat Admin — API Client
// ============================================================

const API_BASE = '/api/admin';

/** Build a query string, dropping undefined/null/empty values. */
function cleanQuery(params?: Record<string, any>): string {
  if (!params) return '';
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    )
  ).toString();
  return qs ? '?' + qs : '';
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      // Exempt the magic-link verify endpoint: a 401 there means "bad/expired
      // token", which the verify page must display in-line rather than
      // bouncing the user to /login and losing the message.
      const isMagicLinkVerify = path.startsWith('/auth/magic-link/verify');
      if (!isMagicLinkVerify) {
        this.token = null;
        localStorage.removeItem('hoverchat_token');
        window.location.href = '/admin/login';
        throw new Error('Unauthorized');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Network error' } }));
      throw new Error(error.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string) {
    const result = await this.request<{ user: any; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.token = result.token;
    localStorage.setItem('hoverchat_token', result.token);
    return result;
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' }).catch(() => {});
    this.token = null;
    localStorage.removeItem('hoverchat_token');
  }

  async requestMagicLink(email: string) {
    return this.request<{ ok: boolean; message: string }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyMagicLink(token: string) {
    const result = await this.request<{ user: any; token: string }>(
      `/auth/magic-link/verify?token=${encodeURIComponent(token)}`,
    );
    this.token = result.token;
    localStorage.setItem('hoverchat_token', result.token);
    return result;
  }

  async getMe() {
    return this.request<{ user: any }>('/auth/me');
  }

  // Dashboard
  async getDashboard() {
    return this.request<{ dashboard: any }>('/dashboard');
  }

  // Tenants
  async listTenants(params?: { page?: number; search?: string; status?: string }) {
    return this.request<{ tenants: any[]; total: number }>(`/tenants${cleanQuery(params)}`);
  }

  async createTenant(data: { name: string; slug: string }) {
    return this.request<{ tenant: any; site_key: any }>('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTenant(id: string) {
    return this.request<{ tenant: any }>(`/tenants/${id}`);
  }

  async updateTenant(id: string, data: any) {
    return this.request<{ tenant: any }>(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTenant(id: string) {
    return this.request(`/tenants/${id}`, { method: 'DELETE' });
  }

  // Domains
  async getDomains(tenantId: string) {
    return this.request<{ domains: any[] }>(`/tenants/${tenantId}/domains`);
  }

  async addDomain(tenantId: string, domain: string) {
    return this.request(`/tenants/${tenantId}/domains`, {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }

  async deleteDomain(tenantId: string, domainId: string) {
    return this.request(`/tenants/${tenantId}/domains/${domainId}`, { method: 'DELETE' });
  }

  // Widget
  async getWidget(tenantId: string) {
    return this.request<{ widget: any }>(`/tenants/${tenantId}/widget`);
  }

  async updateWidget(tenantId: string, data: any) {
    return this.request(`/tenants/${tenantId}/widget`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Theme
  async getTheme(tenantId: string) {
    return this.request<{ theme: any }>(`/tenants/${tenantId}/theme`);
  }

  async updateTheme(tenantId: string, data: any) {
    return this.request(`/tenants/${tenantId}/theme`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Model Config
  async getModelConfig(tenantId: string) {
    return this.request<{ model_config: any }>(`/tenants/${tenantId}/model-config`);
  }

  async updateModelConfig(tenantId: string, data: any) {
    return this.request(`/tenants/${tenantId}/model-config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Prompts
  async getPrompts(tenantId: string) {
    return this.request<{ prompt: any }>(`/tenants/${tenantId}/prompts`);
  }

  async updatePrompts(tenantId: string, data: any) {
    return this.request(`/tenants/${tenantId}/prompts`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Conversations
  async getConversations(tenantId: string, params?: { page?: number; status?: string }) {
    return this.request<{ conversations: any[]; total: number }>(`/tenants/${tenantId}/conversations${cleanQuery(params)}`);
  }

  async getConversation(tenantId: string, convId: string) {
    return this.request<{ messages: any[] }>(`/tenants/${tenantId}/conversations/${convId}`);
  }

  // Analytics
  async getAnalytics(tenantId: string) {
    return this.request<{ analytics: any }>(`/tenants/${tenantId}/analytics`);
  }

  // Feedback
  async getFeedback(tenantId: string, params?: { page?: number }) {
    return this.request<{ feedback: any[]; total: number }>(`/tenants/${tenantId}/feedback${cleanQuery(params)}`);
  }

  // Knowledge Base
  async getKBSources(tenantId: string) {
    return this.request<{ sources: any[] }>(`/tenants/${tenantId}/rag-sources`);
  }

  async addKBText(tenantId: string, data: { name: string; content: string }) {
    return this.request<{ source_id: string; chunk_count: number }>(`/tenants/${tenantId}/kb/text`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadKBFile(tenantId: string, file: File, name?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}/tenants/${tenantId}/kb/upload`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new Error(error.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getKBSourceDetail(tenantId: string, sourceId: string) {
    return this.request<{ source: any; chunks: any[]; totalChunks: number }>(`/tenants/${tenantId}/kb/sources/${sourceId}`);
  }

  async reindexKBSource(tenantId: string, sourceId: string) {
    return this.request<{ success: boolean; chunk_count: number }>(`/tenants/${tenantId}/rag-sources/${sourceId}/reindex`, { method: 'POST' });
  }

  async deleteKBSource(tenantId: string, sourceId: string) {
    return this.request(`/tenants/${tenantId}/rag-sources/${sourceId}`, { method: 'DELETE' });
  }

  async searchKB(tenantId: string, query: string) {
    return this.request<{ results: any[] }>(`/tenants/${tenantId}/kb/search`, {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  }

  // Site Keys
  async getSiteKeys(tenantId: string) {
    return this.request<{ site_keys: any[] }>(`/tenants/${tenantId}/site-keys`);
  }

  async createSiteKey(tenantId: string, label: string) {
    return this.request(`/tenants/${tenantId}/site-keys`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    });
  }

  // Users
  async listUsers(params?: { page?: number }) {
    return this.request<{ users: any[]; total: number }>(`/users${cleanQuery(params)}`);
  }

  async createUser(data: any) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: any) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  // Audit Logs
  async getAuditLogs(params?: { page?: number; action?: string }) {
    return this.request<{ audit_logs: any[]; total: number }>(`/audit-logs${cleanQuery(params)}`);
  }
}

export const api = new ApiClient();

// Restore token on load
const savedToken = localStorage.getItem('hoverchat_token');
if (savedToken) {
  api.setToken(savedToken);
}
